import React, { ReactElement, useRef } from 'react';
import { Pagination as AntPagination } from 'antd';
import styled from 'styled-components';
import { colorsPalette } from '../../styles/colors-palette';
import { Select } from './Select';
import Text from './typography/Text';

type DefaultTotalType = number;

interface PaginationProps<TotalType extends DefaultTotalType> {
  pageNumber: PageNumber;
  pageSize: PageLimit;
  total: TotalType;
  showTotal?: boolean;
  showTotalPrefix?: PlainText;
  renderTotal?: (total: TotalType) => React.ReactNode;
  renderSizeChangerLabel?: (size: number) => string;
  addonBefore?: () => React.ReactNode;
  addonAfter?: () => React.ReactNode;
  hideOnSinglePage?: boolean;
  pageSizeOptions?: number[];
  hideSizeChanger?: boolean;
  disabled?: boolean;
  onPageChange: (page: PageNumber, pageLimit: PageLimit) => void;
  onSizeChange?: (size: PageLimit) => void;
  testIds?: {
    main?: string;
    sizeChanger?: string;
    prev?: string;
    next?: string;
    pageItemPrefix?: string;
    activePage?: string;
  };
}

function Pagination<TotalType extends DefaultTotalType>({
  pageNumber,
  pageSize,
  total,
  showTotal,
  showTotalPrefix,
  renderTotal,
  renderSizeChangerLabel,
  addonBefore,
  addonAfter,
  hideOnSinglePage,
  pageSizeOptions = [10, 30, 50, 100],
  hideSizeChanger = false,
  disabled,
  onPageChange,
  onSizeChange,
  testIds,
}: PaginationProps<TotalType>): ReactElement {
  const wrapperRef = useRef<HTMLDivElement>(null);

  const scrollToTop = () => {
    const root = wrapperRef.current?.getRootNode();
    if (root instanceof ShadowRoot) {
      (root.host as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    const scrollTarget = (root as Document)?.querySelector<HTMLElement>('[data-scroll-target]');
    if (scrollTarget) {
      scrollTarget.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const antOnChange = (pageNumber: number, pageSize: number) => {
    scrollToTop();
    onPageChange && onPageChange((pageNumber - 1) as PageNumber, pageSize as PageLimit);
  };

  const antOnShowSizeChange = (pageSize: number) => {
    scrollToTop();
    onSizeChange && onSizeChange(pageSize as PageLimit);
  };

  const antRenderTotal = () => {
    if (renderTotal && typeof renderTotal(total) === 'string') {
      return <Text>{renderTotal(total)}</Text>;
    }

    return <>{renderTotal && renderTotal(total)}</>;
  };

  const sizeChangerOptions = pageSizeOptions.map(item => {
    return {
      label: renderSizeChangerLabel ? renderSizeChangerLabel(item) : item.toString(),
      value: item as PageLimit,
    };
  });

  if ((hideOnSinglePage && Math.floor(total / pageSize) < 1) || !total) {
    return <></>;
  }

  const hasItemTestIds =
    testIds?.prev || testIds?.next || testIds?.pageItemPrefix || testIds?.activePage;

  const itemRender = hasItemTestIds
    ? (
        page: number,
        type: 'page' | 'prev' | 'next' | 'jump-prev' | 'jump-next',
        originalElement: React.ReactNode,
      ) => {
        let itemTestId: string | undefined;

        if (type === 'prev' && testIds?.prev) {
          itemTestId = testIds.prev;
        } else if (type === 'next' && testIds?.next) {
          itemTestId = testIds.next;
        } else if (type === 'page') {
          const isActive = page === pageNumber + 1;
          if (isActive && testIds?.activePage) {
            itemTestId = testIds.activePage;
          } else if (testIds?.pageItemPrefix) {
            itemTestId = `${testIds.pageItemPrefix}${page}`;
          }
        }

        if (itemTestId) {
          return (
            <PaginationItemWrapper data-testid={itemTestId}>
              {originalElement}
            </PaginationItemWrapper>
          );
        }

        return originalElement;
      }
    : undefined;

  return (
    <PaginationWrapper ref={wrapperRef} data-testid={testIds?.main}>
      {addonBefore && addonBefore()}
      {renderTotal && antRenderTotal()}
      <StyledPagination
        pageSize={pageSize}
        current={pageNumber + 1}
        total={total}
        showTotal={() =>
          showTotal && (
            <Text type="secondary">
              {showTotalPrefix} {total}
            </Text>
          )
        }
        pageSizeOptions={pageSizeOptions}
        showSizeChanger={false}
        disabled={disabled}
        onChange={antOnChange}
        onShowSizeChange={antOnShowSizeChange}
        itemRender={itemRender}
      />
      {!hideSizeChanger && (
        <SizeChangerWrapper>
          <StyledSelect
            showSearch={false}
            options={sizeChangerOptions}
            value={pageSize}
            onChange={antOnShowSizeChange}
            testId={testIds?.sizeChanger}
          />
        </SizeChangerWrapper>
      )}
      {addonAfter && addonAfter()}
    </PaginationWrapper>
  );
}

const PaginationWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const SizeChangerWrapper = styled.div`
  margin-left: 16px;
`;

const StyledSelect = styled(Select)`
  &.ant-select-single.ant-select-lg {
    height: 32px;
    font-size: 14px;
  }
` as typeof Select;

const PaginationItemWrapper = styled.span``;

const StyledPagination = styled(AntPagination)`
  &.ant-pagination {
    margin: 16px 0;
  }

  &.ant-pagination > .ant-pagination-item-active {
    border-color: ${colorsPalette.dustyBlue500};
  }

  &.ant-pagination .ant-pagination-item-active a {
    color: ${colorsPalette.dustyBlue500};
  }
`;

export { Pagination };
